import {
  ANNUAL_MULTI_TRIP,
  BASIC,
  COMPREHENSIVE,
  SIGNATURE,
} from "@/constants";
import { CoverLevelBenefits } from "@/schema/renewal-page/middle-panel.schema";
import { DynamicCoverLimit } from "@/types/DynamicCoverLimitType";
import { Attributes } from "@/types/quote/quote-data";
import isCruiseImpact from "@/utils/check-cruise-impact-base-on-brand.utils";
import { getCoverLevelBenefits } from "@/utils/get-cover-level-info";
import { quoteShouldHideField } from "@/utils/quote-brand-impact.utlls";

import {
  ComparisonCardData,
  ComparisonCoverType,
} from "./cover-level-comparison-card";

const COMPARISON_ORDER: ComparisonCoverType[] = [
  BASIC,
  COMPREHENSIVE,
  SIGNATURE,
];

// TODO: extract to brand-aware util when expanding beyond staysure UK
const CURRENCY_SYMBOL = "£";

function formatCurrency(value: number | null | undefined, symbol: string) {
  return value ? `${symbol}${value.toFixed(2)}` : "";
}

function findCancellationDisplayValue(
  dynamicCoverLimit: DynamicCoverLimit[] | undefined,
  coverType: string
) {
  return (
    dynamicCoverLimit
      ?.flatMap((cover) =>
        cover.product.filter(
          (item) =>
            item.policyType === ANNUAL_MULTI_TRIP &&
            item.policyPackage === coverType
        )
      )
      .flatMap((product) => product.values)
      .find((value) => value.selected)?.displayValue ?? null
  );
}

export default function buildComparisonCards(
  coverLevelBenefits: CoverLevelBenefits,
  attributes: Attributes
): ComparisonCardData[] {
  const { tripDetails, priceResult } = attributes;
  const isCruise = Boolean(tripDetails?.goingOnCruise && isCruiseImpact);
  const hideUKExcludedFields = Boolean(quoteShouldHideField(tripDetails));

  return COMPARISON_ORDER.map((coverType) => {
    const cover = getCoverLevelBenefits(
      coverLevelBenefits,
      coverType,
      isCruise
    );

    const benefitCopies = cover?.benefitCopy ?? {};
    const visibleBenefits = Object.keys(benefitCopies)
      .filter((key) => !isNaN(key as unknown as number))
      .map((key) => benefitCopies[key])
      .filter((b) => b.isShownInRenewals)
      .filter((b) => !(hideUKExcludedFields && b.isExcludeUK));

    const allBullets = visibleBenefits.map((benefit) => {
      if (!benefit.isCancellation) return benefit.description;
      const amount = findCancellationDisplayValue(
        priceResult?.dynamicCoverLimit,
        coverType
      );
      return `${amount ?? ""} ${benefit.description}`.trim();
    });

    const bullets = allBullets
      ?.filter((bullet) => !bullet.toLowerCase().includes("doctor"))
      .slice(0, 3);
    const coverPrice = priceResult?.policyPackagePrice?.find(
      (p) => p.policyType === ANNUAL_MULTI_TRIP && p.policyPackage === coverType
    );

    return {
      coverType,
      coverName: cover?.coverName ?? "",
      totalPrice: coverPrice
        ? formatCurrency(
            coverPrice.onlinePresentationTotalWithDiscount ?? coverPrice.total,
            CURRENCY_SYMBOL
          )
        : "",
      perMonthPrice: coverPrice
        ? formatCurrency(coverPrice.collectionAmount, CURRENCY_SYMBOL)
        : "",
      bullets,
    };
  });
}
